import { c as _c } from "react/compiler-runtime";
import { B3_TOKEN, getDefaultToken, USDC_BASE } from "../../../anyspend/index.js";
import { useAnyspendCreateOnrampOrder, useAnyspendCreateOrder, useAnyspendOrderAndTransactions, useAnyspendQuote, useGeoOnrampOptions } from "../../../anyspend/react/index.js";
import { anyspendService } from "../../../anyspend/services/anyspend.js";
import { normalizeAddress } from "../../../anyspend/utils/index.js";
import { toast, useAccountWallet, useProfile, useRouter, useSearchParamsSSR, useTokenBalance } from "../../../global-account/react/index.js";
import { formatTokenAmount, formatUnits } from "../../../shared/utils/number.js";
import { useEffect, useMemo, useState } from "react";
import { encodeFunctionData, parseUnits } from "viem";
import { base, mainnet } from "viem/chains";
import { CryptoPaymentMethodType } from "../components/common/CryptoPaymentMethod.js";
import { FiatPaymentMethod } from "../components/common/FiatPaymentMethod.js";
import { useAutoSelectCryptoPaymentMethod } from "./useAutoSelectCryptoPaymentMethod.js";
import { useConnectedWalletDisplay } from "./useConnectedWalletDisplay.js";
import { useCryptoPaymentMethodState } from "./useCryptoPaymentMethodState.js";
import { useRecipientAddressState } from "./useRecipientAddressState.js";
export var PanelView;
(function (PanelView) {
  PanelView[PanelView["MAIN"] = 0] = "MAIN";
  PanelView[PanelView["CRYPTO_PAYMENT_METHOD"] = 1] = "CRYPTO_PAYMENT_METHOD";
  PanelView[PanelView["FIAT_PAYMENT_METHOD"] = 2] = "FIAT_PAYMENT_METHOD";
  PanelView[PanelView["RECIPIENT_SELECTION"] = 3] = "RECIPIENT_SELECTION";
  PanelView[PanelView["ORDER_DETAILS"] = 4] = "ORDER_DETAILS";
  PanelView[PanelView["LOADING"] = 5] = "LOADING";
  PanelView[PanelView["POINTS_DETAIL"] = 6] = "POINTS_DETAIL";
  PanelView[PanelView["FEE_DETAIL"] = 7] = "FEE_DETAIL";
  PanelView[PanelView["DIRECT_TRANSFER_SUCCESS"] = 8] = "DIRECT_TRANSFER_SUCCESS";
})(PanelView || (PanelView = {}));
/**
 * Generates encoded function data for custom contract calls.
 * Handles amount placeholder replacement and BigInt conversion.
 */
export function generateEncodedData(config, amountInWei) {
  if (!config || !config.functionAbi || !config.functionName || !config.functionArgs) {
    console.warn("customExactInConfig missing required fields for encoding:", {
      hasConfig: !!config,
      hasFunctionAbi: !!config?.functionAbi,
      hasFunctionName: !!config?.functionName,
      hasFunctionArgs: !!config?.functionArgs
    });
    return undefined;
  }
  try {
    const abi = JSON.parse(config.functionAbi);
    const processedArgs = config.functionArgs.map(arg => {
      // Replace amount placeholders ({{dstAmount}}, {{amount_out}}, etc.)
      if (arg === "{{dstAmount}}" || arg === "{{amount_out}}") {
        return BigInt(amountInWei);
      }
      // Convert numeric strings to BigInt for uint256 args
      if (/^\d+$/.test(arg)) {
        return BigInt(arg);
      }
      return arg;
    });
    return encodeFunctionData({
      abi,
      functionName: config.functionName,
      args: processedArgs
    });
  } catch (e) {
    console.error("Failed to encode function data:", e, {
      functionAbi: config.functionAbi,
      functionName: config.functionName,
      functionArgs: config.functionArgs
    });
    return undefined;
  }
}
// This hook serves for order hype_duel and custom_exact_in
export function useAnyspendFlow(t0) {
  const $ = _c(96);
  const {
    paymentType: t1,
    recipientAddress,
    loadOrder,
    onOrderSuccess,
    onTransactionSuccess,
    sourceTokenAddress,
    sourceTokenChainId,
    destinationTokenAddress,
    destinationTokenChainId,
    slippage: t2,
    disableUrlParamManagement: t3,
    orderType: t4,
    customExactInConfig,
    senderAddress
  } = t0;
  const paymentType = t1 === undefined ? "crypto" : t1;
  const slippage = t2 === undefined ? 0 : t2;
  const disableUrlParamManagement = t3 === undefined ? false : t3;
  const orderType = t4 === undefined ? "hype_duel" : t4;
  const searchParams = useSearchParamsSSR();
  const router = useRouter();
  const [activePanel, setActivePanel] = useState(loadOrder ? PanelView.ORDER_DETAILS : PanelView.MAIN);
  const [orderId, setOrderId] = useState(loadOrder);
  const {
    orderAndTransactions: oat
  } = useAnyspendOrderAndTransactions(orderId);
  const [selectedSrcChainId, setSelectedSrcChainId] = useState(sourceTokenChainId || (paymentType === "fiat" ? base.id : mainnet.id));
  let t5;
  if ($[0] !== paymentType || $[1] !== selectedSrcChainId) {
    t5 = paymentType === "fiat" ? USDC_BASE : getDefaultToken(selectedSrcChainId);
    $[0] = paymentType;
    $[1] = selectedSrcChainId;
    $[2] = t5;
  } else {
    t5 = $[2];
  }
  const defaultSrcToken = t5;
  const [selectedSrcToken, setSelectedSrcToken] = useState(defaultSrcToken);
  const [selectedDstToken, setSelectedDstToken] = useState(B3_TOKEN);
  const [srcAmount, setSrcAmount] = useState(paymentType === "fiat" ? "5" : "0");
  const [dstAmount, setDstAmount] = useState("");
  const [dstAmountInput, setDstAmountInput] = useState("");
  const [debouncedDstAmountInput, setDebouncedDstAmountInput] = useState("");
  const [isSrcInputDirty, setIsSrcInputDirty] = useState(true);
  const selectedDstChainId = destinationTokenChainId || selectedDstToken.chainId;
  const {
    cryptoPaymentMethod,
    setCryptoPaymentMethod,
    selectedCryptoPaymentMethod,
    setSelectedCryptoPaymentMethod,
    effectiveCryptoPaymentMethod,
    resetPaymentMethods
  } = useCryptoPaymentMethodState();
  const [selectedFiatPaymentMethod, setSelectedFiatPaymentMethod] = useState(FiatPaymentMethod.NONE);
  const {
    address: globalAddress
  } = useAccountWallet();
  const {
    walletAddress
  } = useConnectedWalletDisplay(effectiveCryptoPaymentMethod);
  let t6;
  if ($[3] !== globalAddress || $[4] !== recipientAddress || $[5] !== walletAddress) {
    t6 = {
      recipientAddressFromProps: recipientAddress,
      walletAddress,
      globalAddress
    };
    $[3] = globalAddress;
    $[4] = recipientAddress;
    $[5] = walletAddress;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  const {
    setSelectedRecipientAddress,
    effectiveRecipientAddress
  } = useRecipientAddressState(t6);
  let t7;
  if ($[7] !== effectiveRecipientAddress) {
    t7 = {
      address: effectiveRecipientAddress,
      fresh: true
    };
    $[7] = effectiveRecipientAddress;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  const recipientProfile = useProfile(t7);
  const recipientName = recipientProfile.data?.name;
  const effectiveBalanceAddress = senderAddress || walletAddress;
  let t8;
  if ($[9] !== effectiveBalanceAddress || $[10] !== selectedSrcToken) {
    t8 = {
      token: selectedSrcToken,
      address: effectiveBalanceAddress
    };
    $[9] = effectiveBalanceAddress;
    $[10] = selectedSrcToken;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  const {
    rawBalance,
    isLoading: isBalanceLoading
  } = useTokenBalance(t8);
  let t9;
  bb0: {
    if (!rawBalance || isBalanceLoading || paymentType !== "crypto") {
      t9 = false;
      break bb0;
    }
    try {
      const requiredAmount = parseUnits(srcAmount.replace(/,/g, ""), selectedSrcToken.decimals);
      t9 = rawBalance >= requiredAmount;
    } catch {
      t9 = false;
    }
  }
  const hasEnoughBalance = t9;
  let t10;
  if ($[12] !== cryptoPaymentMethod || $[13] !== hasEnoughBalance || $[14] !== isBalanceLoading || $[15] !== paymentType || $[16] !== selectedCryptoPaymentMethod || $[17] !== setCryptoPaymentMethod) {
    t10 = {
      paymentType,
      cryptoPaymentMethod,
      setCryptoPaymentMethod,
      selectedCryptoPaymentMethod,
      hasEnoughBalance,
      isBalanceLoading
    };
    $[12] = cryptoPaymentMethod;
    $[13] = hasEnoughBalance;
    $[14] = isBalanceLoading;
    $[15] = paymentType;
    $[16] = selectedCryptoPaymentMethod;
    $[17] = setCryptoPaymentMethod;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  useAutoSelectCryptoPaymentMethod(t10);
  let t11;
  let t12;
  if ($[19] !== sourceTokenAddress || $[20] !== sourceTokenChainId) {
    t11 = () => {
      const fetchSourceToken = async () => {
        if (sourceTokenAddress && sourceTokenChainId) {
          ;
          try {
            const token = await anyspendService.getToken(sourceTokenChainId, sourceTokenAddress);
            setSelectedSrcToken(token);
          } catch (t13) {
            const error = t13;
            console.error("Failed to fetch source token:", error);
            toast.error(`Failed to load token ${sourceTokenAddress} on chain ${sourceTokenChainId}`);
          }
        }
      };
      fetchSourceToken();
    };
    t12 = [sourceTokenAddress, sourceTokenChainId];
    $[19] = sourceTokenAddress;
    $[20] = sourceTokenChainId;
    $[21] = t11;
    $[22] = t12;
  } else {
    t11 = $[21];
    t12 = $[22];
  }
  useEffect(t11, t12);
  let t13;
  let t14;
  if ($[23] !== destinationTokenAddress || $[24] !== destinationTokenChainId) {
    t13 = () => {
      const fetchDestinationToken = async () => {
        if (destinationTokenAddress && destinationTokenChainId) {
          ;
          try {
            const token_0 = await anyspendService.getToken(destinationTokenChainId, destinationTokenAddress);
            setSelectedDstToken(token_0);
          } catch (t15) {
            const error_0 = t15;
            console.error("Failed to fetch destination token:", error_0);
            toast.error(`Failed to load token ${destinationTokenAddress} on chain ${destinationTokenChainId}`);
          }
        }
      };
      fetchDestinationToken();
    };
    t14 = [destinationTokenAddress, destinationTokenChainId];
    $[23] = destinationTokenAddress;
    $[24] = destinationTokenChainId;
    $[25] = t13;
    $[26] = t14;
  } else {
    t13 = $[25];
    t14 = $[26];
  }
  useEffect(t13, t14);
  const isDestinationTokenReady = !destinationTokenAddress || selectedDstToken.address.toLowerCase() === destinationTokenAddress.toLowerCase();
  let t15;
  let t16;
  if ($[27] !== dstAmountInput) {
    t15 = () => {
      const timer = setTimeout(() => {
        setDebouncedDstAmountInput(dstAmountInput);
      }, 500);
      return () => clearTimeout(timer);
    };
    t16 = [dstAmountInput];
    $[27] = dstAmountInput;
    $[28] = t15;
    $[29] = t16;
  } else {
    t15 = $[28];
    t16 = $[29];
  }
  useEffect(t15, t16);
  const getOnrampVendor = _temp;
  const effectiveSrcDecimals = paymentType === "fiat" ? USDC_BASE.decimals : selectedSrcToken.decimals;
  const activeInputAmountInWei = parseUnits(srcAmount.replace(/,/g, ""), effectiveSrcDecimals).toString();
  const activeOutputAmountInWei = isDestinationTokenReady ? parseUnits(debouncedDstAmountInput.replace(/,/g, "") || "0", selectedDstToken.decimals).toString() : "0";
  const tradeType = isSrcInputDirty ? "EXACT_INPUT" : "EXACT_OUTPUT";
  let t17;
  bb1: {
    const baseParams = {
      srcChain: paymentType === "fiat" ? base.id : selectedSrcChainId,
      dstChain: selectedDstChainId ?? base.id,
      srcTokenAddress: paymentType === "fiat" ? USDC_BASE.address : selectedSrcToken.address,
      dstTokenAddress: selectedDstToken.address,
      recipientAddress: effectiveRecipientAddress,
      onrampVendor: paymentType === "fiat" ? getOnrampVendor(selectedFiatPaymentMethod) : undefined
    };
    if (orderType === "swap") {
      t17 = {
        ...baseParams,
        type: "swap",
        tradeType,
        amount: tradeType === "EXACT_INPUT" ? activeInputAmountInWei : activeOutputAmountInWei
      };
    } else {
      if (orderType === "hype_duel") {
        t17 = {
          ...baseParams,
          type: "hype_duel",
          amount: activeInputAmountInWei
        };
      } else {
        if (tradeType === "EXACT_OUTPUT") {
          const encodedData = generateEncodedData(customExactInConfig, activeOutputAmountInWei);
          t17 = {
            ...baseParams,
            type: "custom",
            payload: {
              amount: activeOutputAmountInWei,
              data: encodedData || "",
              to: customExactInConfig ? normalizeAddress(customExactInConfig.to) : "",
              spenderAddress: customExactInConfig?.spenderAddress ? normalizeAddress(customExactInConfig.spenderAddress) : undefined
            }
          };
          break bb1;
        }
        t17 = {
          ...baseParams,
          type: "custom_exact_in",
          amount: activeInputAmountInWei
        };
      }
    }
  }
  const quoteRequest = t17;
  const {
    anyspendQuote,
    isLoadingAnyspendQuote,
    getAnyspendQuoteError
  } = useAnyspendQuote(quoteRequest);
  const isDebouncingDstAmount = tradeType === "EXACT_OUTPUT" && dstAmountInput !== debouncedDstAmountInput;
  const isQuoteLoading = isLoadingAnyspendQuote || isDebouncingDstAmount;
  const {
    geoData,
    coinbaseAvailablePaymentMethods,
    stripeWeb2Support
  } = useGeoOnrampOptions(paymentType === "fiat" ? formatUnits(activeInputAmountInWei, USDC_BASE.decimals) : "0");
  let t18;
  let t19;
  if ($[30] !== anyspendQuote || $[31] !== dstAmountInput || $[32] !== isSrcInputDirty || $[33] !== slippage) {
    t18 = () => {
      if (isSrcInputDirty) {
        if (anyspendQuote?.data?.currencyOut?.amount && anyspendQuote.data.currencyOut.currency?.decimals) {
          const amount = anyspendQuote.data.currencyOut.amount;
          const decimals = anyspendQuote.data.currencyOut.currency.decimals;
          const amountWithSlippage = BigInt(amount) * BigInt(100 - slippage) / BigInt(100);
          const formattedAmount = formatTokenAmount(amountWithSlippage, decimals, 6, false);
          setDstAmount(formattedAmount);
        } else {
          setDstAmount("");
        }
      } else {
        if (anyspendQuote?.data?.currencyIn?.amount && anyspendQuote.data.currencyIn.currency?.decimals) {
          const amount_0 = anyspendQuote.data.currencyIn.amount;
          const decimals_0 = anyspendQuote.data.currencyIn.currency.decimals;
          const formattedAmount_0 = formatTokenAmount(BigInt(amount_0), decimals_0, 6, false);
          setSrcAmount(formattedAmount_0);
        }
        setDstAmount(dstAmountInput);
      }
    };
    t19 = [anyspendQuote, slippage, isSrcInputDirty, dstAmountInput];
    $[30] = anyspendQuote;
    $[31] = dstAmountInput;
    $[32] = isSrcInputDirty;
    $[33] = slippage;
    $[34] = t18;
    $[35] = t19;
  } else {
    t18 = $[34];
    t19 = $[35];
  }
  useEffect(t18, t19);
  let t20;
  let t21;
  if ($[36] !== disableUrlParamManagement || $[37] !== loadOrder || $[38] !== searchParams) {
    t20 = () => {
      if (loadOrder || disableUrlParamManagement) {
        return;
      }
      const orderIdParam = searchParams.get("orderId");
      if (orderIdParam) {
        setOrderId(orderIdParam);
        setActivePanel(PanelView.ORDER_DETAILS);
      }
    };
    t21 = [searchParams, loadOrder, disableUrlParamManagement];
    $[36] = disableUrlParamManagement;
    $[37] = loadOrder;
    $[38] = searchParams;
    $[39] = t20;
    $[40] = t21;
  } else {
    t20 = $[39];
    t21 = $[40];
  }
  useEffect(t20, t21);
  let t22;
  if ($[41] !== disableUrlParamManagement || $[42] !== effectiveCryptoPaymentMethod || $[43] !== onOrderSuccess || $[44] !== router || $[45] !== searchParams) {
    t22 = {
      onSuccess: data => {
        const newOrderId = data.data.id;
        setOrderId(newOrderId);
        setActivePanel(PanelView.ORDER_DETAILS);
        onOrderSuccess?.(newOrderId);
        if (!disableUrlParamManagement) {
          const params = new URLSearchParams(searchParams.toString());
          params.set("orderId", newOrderId);
          if (effectiveCryptoPaymentMethod !== CryptoPaymentMethodType.NONE) {
            console.log("Setting cryptoPaymentMethod in URL:", effectiveCryptoPaymentMethod);
            params.set("cryptoPaymentMethod", effectiveCryptoPaymentMethod);
          } else {
            console.log("Payment method is NONE, not setting in URL");
          }
          console.log("Final URL params:", params.toString());
          router.push(`${window.location.pathname}?${params.toString()}`);
        }
      },
      onError: _temp2
    };
    $[41] = disableUrlParamManagement;
    $[42] = effectiveCryptoPaymentMethod;
    $[43] = onOrderSuccess;
    $[44] = router;
    $[45] = searchParams;
    $[46] = t22;
  } else {
    t22 = $[46];
  }
  const {
    createOrder,
    isCreatingOrder
  } = useAnyspendCreateOrder(t22);
  let t23;
  if ($[47] !== onOrderSuccess) {
    t23 = {
      onSuccess: data_0 => {
        const newOrderId_0 = data_0.data.id;
        setOrderId(newOrderId_0);
        setActivePanel(PanelView.ORDER_DETAILS);
        onOrderSuccess?.(newOrderId_0);
      },
      onError: _temp3
    };
    $[47] = onOrderSuccess;
    $[48] = t23;
  } else {
    t23 = $[48];
  }
  const {
    createOrder: createOnrampOrder,
    isCreatingOrder: isCreatingOnrampOrder
  } = useAnyspendCreateOnrampOrder(t23);
  let t24;
  if ($[49] !== oat || $[50] !== onTransactionSuccess) {
    t24 = () => {
      if (oat?.data?.order.status === "executed") {
        const amount_1 = oat.data.order.settlement?.actualDstAmount;
        const formattedActualDstAmount = amount_1 ? formatTokenAmount(BigInt(amount_1), oat.data.order.metadata.dstToken.decimals) : undefined;
        onTransactionSuccess?.(formattedActualDstAmount ?? "");
      }
    };
    $[49] = oat;
    $[50] = onTransactionSuccess;
    $[51] = t24;
  } else {
    t24 = $[51];
  }
  const t25 = oat?.data?.order.status;
  const t26 = oat?.data?.order.settlement?.actualDstAmount;
  const t27 = oat?.data?.order.metadata.dstToken.decimals;
  let t28;
  if ($[52] !== onTransactionSuccess || $[53] !== t25 || $[54] !== t26 || $[55] !== t27) {
    t28 = [t25, t26, onTransactionSuccess, t27];
    $[52] = onTransactionSuccess;
    $[53] = t25;
    $[54] = t26;
    $[55] = t27;
    $[56] = t28;
  } else {
    t28 = $[56];
  }
  useEffect(t24, t28);
  let t29;
  if ($[57] !== activeInputAmountInWei || $[58] !== activeOutputAmountInWei || $[59] !== activePanel || $[60] !== anyspendQuote || $[61] !== coinbaseAvailablePaymentMethods || $[62] !== createOnrampOrder || $[63] !== createOrder || $[64] !== cryptoPaymentMethod || $[65] !== dstAmount || $[66] !== dstAmountInput || $[67] !== effectiveCryptoPaymentMethod || $[68] !== effectiveRecipientAddress || $[69] !== geoData || $[70] !== getAnyspendQuoteError || $[71] !== globalAddress || $[72] !== hasEnoughBalance || $[73] !== isBalanceLoading || $[74] !== isCreatingOnrampOrder || $[75] !== isCreatingOrder || $[76] !== isLoadingAnyspendQuote || $[77] !== isQuoteLoading || $[78] !== isSrcInputDirty || $[79] !== oat || $[80] !== orderId || $[81] !== recipientName || $[82] !== resetPaymentMethods || $[83] !== selectedCryptoPaymentMethod || $[84] !== selectedDstChainId || $[85] !== selectedDstToken || $[86] !== selectedFiatPaymentMethod || $[87] !== selectedSrcChainId || $[88] !== selectedSrcToken || $[89] !== setCryptoPaymentMethod || $[90] !== setSelectedCryptoPaymentMethod || $[91] !== setSelectedRecipientAddress || $[92] !== srcAmount || $[93] !== stripeWeb2Support || $[94] !== tradeType) {
    t29 = {
      activePanel,
      setActivePanel,
      orderId,
      setOrderId,
      oat,
      selectedSrcChainId,
      setSelectedSrcChainId,
      selectedDstChainId,
      selectedSrcToken,
      setSelectedSrcToken,
      selectedDstToken,
      setSelectedDstToken,
      srcAmount,
      setSrcAmount,
      dstAmount,
      setDstAmount,
      dstAmountInput,
      setDstAmountInput,
      isSrcInputDirty,
      setIsSrcInputDirty,
      tradeType,
      cryptoPaymentMethod,
      setCryptoPaymentMethod,
      selectedCryptoPaymentMethod,
      setSelectedCryptoPaymentMethod,
      effectiveCryptoPaymentMethod,
      resetPaymentMethods,
      selectedFiatPaymentMethod,
      setSelectedFiatPaymentMethod,
      selectedRecipientAddress: effectiveRecipientAddress,
      setSelectedRecipientAddress,
      recipientName,
      globalAddress,
      hasEnoughBalance,
      isBalanceLoading,
      anyspendQuote,
      isLoadingAnyspendQuote,
      isQuoteLoading,
      getAnyspendQuoteError,
      activeInputAmountInWei,
      activeOutputAmountInWei,
      geoData,
      coinbaseAvailablePaymentMethods,
      stripeWeb2Support,
      getOnrampVendor,
      createOrder,
      isCreatingOrder,
      createOnrampOrder,
      isCreatingOnrampOrder
    };
    $[57] = activeInputAmountInWei;
    $[58] = activeOutputAmountInWei;
    $[59] = activePanel;
    $[60] = anyspendQuote;
    $[61] = coinbaseAvailablePaymentMethods;
    $[62] = createOnrampOrder;
    $[63] = createOrder;
    $[64] = cryptoPaymentMethod;
    $[65] = dstAmount;
    $[66] = dstAmountInput;
    $[67] = effectiveCryptoPaymentMethod;
    $[68] = effectiveRecipientAddress;
    $[69] = geoData;
    $[70] = getAnyspendQuoteError;
    $[71] = globalAddress;
    $[72] = hasEnoughBalance;
    $[73] = isBalanceLoading;
    $[74] = isCreatingOnrampOrder;
    $[75] = isCreatingOrder;
    $[76] = isLoadingAnyspendQuote;
    $[77] = isQuoteLoading;
    $[78] = isSrcInputDirty;
    $[79] = oat;
    $[80] = orderId;
    $[81] = recipientName;
    $[82] = resetPaymentMethods;
    $[83] = selectedCryptoPaymentMethod;
    $[84] = selectedDstChainId;
    $[85] = selectedDstToken;
    $[86] = selectedFiatPaymentMethod;
    $[87] = selectedSrcChainId;
    $[88] = selectedSrcToken;
    $[89] = setCryptoPaymentMethod;
    $[90] = setSelectedCryptoPaymentMethod;
    $[91] = setSelectedRecipientAddress;
    $[92] = srcAmount;
    $[93] = stripeWeb2Support;
    $[94] = tradeType;
    $[95] = t29;
  } else {
    t29 = $[95];
  }
  return t29;
}
function _temp3(error_2) {
  console.error(error_2);
  toast.error("Failed to create order: " + error_2.message);
}
function _temp2(error_1) {
  console.error(error_1);
  toast.error("Failed to create order: " + error_1.message);
}
function _temp(paymentMethod) {
  switch (paymentMethod) {
    case FiatPaymentMethod.COINBASE_PAY:
      {
        return "coinbase";
      }
    case FiatPaymentMethod.STRIPE:
      {
        return "stripe-web2";
      }
    default:
      {
        return;
      }
  }
}